import React, { useMemo, useState, useEffect } from "react";
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Tag,
  message,
  InputNumber,
  Table,
  Modal,
  Popconfirm,
  Select,
} from "antd";
import {
  EditOutlined,
  EyeOutlined,
  DeleteOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  getProductos,
  createProducto,
  updateProducto,
  deleteProducto,
} from "../../../services/financiero/productos/getProductos";
import Header166 from "../../header/header";

const ProductosSostenibilidad = () => {
  const [productos, setProductos] = useState([]);
  const [filteredProductos, setFilteredProductos] = useState([]); // Estado para los datos filtrados
  const [searchTerm, setSearchTerm] = useState(""); // Estado del buscador
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentProducto, setCurrentProducto] = useState(null);
  const [form] = Form.useForm();

  const [categoryFilter, setCategoryFilter] = useState(null); // Estado del filtro de categoría
  const [categorias, setCategorias] = useState([]);

  const [unidadesMedida, setUnidadesMedida] = useState([]); // ← nuevo
  const [tiposProducto, setTiposProducto] = useState([]);

  const [selectedProductDetails, setSelectedProductDetails] = useState(null);
  const [isModalVisibleP, setIsModalVisibleP] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  // 1. Fetch productos
  const fetchProductos = async () => {
    setLoading(true);
    try {
      const response = await getProductos();

      setProductos(response);
      setFilteredProductos(response);
    } catch {
      message.error("Error al cargar productos.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProductos();
  }, []);

  // 2. Cuando cambien los productos, extrae los listados únicos
  useEffect(() => {
    // Categorías
    const cats = productos.reduce((acc, p) => {
      if (!acc.some((c) => c.oid === p.oid_categoria)) {
        acc.push({ oid: p.oid_categoria, nombreCategoria: p.nombreCategoria });
      }
      return acc;
    }, []);
    setCategorias(cats);

    // Unidades de Medida
    const uds = productos.reduce((acc, p) => {
      if (!acc.some((u) => u.oid === p.oid_unidad_medida)) {
        acc.push({
          oid: p.oid_unidad_medida,
          nombreUnidadMedida: p.nombreUnidadMedida,
        });
      }
      return acc;
    }, []);
    setUnidadesMedida(uds);

    // Tipos de Producto
    const tipos = productos.reduce((acc, p) => {
      if (!acc.some((t) => t.oid === p.oid_fin_tipoProducto)) {
        acc.push({
          oid: p.oid_fin_tipoProducto,
          nombreTipoProducto: p.nombreTipoProducto,
        });
      }
      return acc;
    }, []);
    setTiposProducto(tipos);
  }, [productos]);

  // 3. Filtrado combinado (búsqueda + categoría)
  useEffect(() => {
    const term = searchTerm.trim().toLowerCase();
    const filtered = productos.filter((producto) => {
      const nombre = producto.nombre_producto.toLowerCase();
      const codigo = producto.codigo.toLowerCase();

      // coincidencia por nombre o por código
      const matchesSearch = nombre.includes(term) || codigo.includes(term);

      const matchesCategory = categoryFilter
        ? producto.nombreCategoria === categoryFilter
        : true;

      return matchesSearch && matchesCategory;
    });
    setFilteredProductos(filtered);
  }, [searchTerm, categoryFilter, productos]);

  // 4. Opcional: si sigues usando categoríasUnicas para tu filtro de búsqueda
  const categoriasUnicas = useMemo(() => {
    return Array.from(
      new Set(productos.map((p) => p.nombreCategoria).filter(Boolean))
    );
  }, [productos]);

  const handleSubmit = async (values) => {
    try {
      if (isEditing) {
        await updateProducto(currentProducto.oid, values);
        message.success("Producto actualizado correctamente.");
      } else {
        await createProducto(values);
        message.success("Producto creado correctamente.");
      }
      fetchProductos();
      setIsModalOpen(false);
      form.resetFields();
    } catch {
      message.error("Error al guardar el producto.");
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteProducto(id);
      message.success("Producto eliminado correctamente.");
      fetchProductos();
    } catch {
      message.error("Error al eliminar el producto.");
    }
  };

  const handlePreview = (plan) => {
    setSelectedProductDetails(plan);
    setIsModalVisibleP(true);
  };

  const columns = [
    {
      title: "Acciones",
      key: "actions",
      with: 100,
      render: (text, record) => (
        <div className="d-flex gap-2">
          <Button
            type="primary"
            icon={<EyeOutlined />}
            onClick={() => handlePreview(record)}
          />
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => {
              setIsEditing(true);
              setCurrentProducto(record);
              form.setFieldsValue(record);
              setIsModalOpen(true);
            }}
          />

          <Popconfirm
            title="¿Estás segur@ de eliminar este ítem?"
            onConfirm={() => handleDelete(record.oid)}
            okText="Sí"
            cancelText="No"
          >
            <Button type="primary" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </div>
      ),
    },
    {
      title: "ID",
      dataIndex: "oid",
      key: "oid",
      width: 60,
    },
    {
      title: "Código",
      dataIndex: "codigo",
      key: "codigo",
      width: 100,
    },
    {
      title: "Nombre",
      dataIndex: "nombre_producto",
      key: "nombre_producto",
      with: 250,
    },
    {
      title: "Categoría",
      dataIndex: "nombreCategoria",
      key: "nombreCategoria",
    },
    {
      title: "Clasificación",
      dataIndex: "clasificacion",
      key: "clasificacion",
    },
    {
      title: "Tipo de Producto",
      dataIndex: "nombreTipoProducto",
      key: "nombreTipoProducto",
    },
    {
      title: "Unidad de Medida",
      dataIndex: "nombreUnidadMedida",
      key: "nombreUnidadMedida",
    },
    {
      title: "Unidad Técnica",
      dataIndex: "unidad_tecnica",
      key: "unidad_tecnica",
    },
    {
      title: "Símbolo Proyecto",
      dataIndex: "simbolo_proyecto",
      key: "simbolo_proyecto",
    },
    {
      title: "Precio (COP)",
      dataIndex: "precio_unitarioPesos",
      key: "precio_unitarioPesos",
      align: "right",
      render: (value) =>
        new Intl.NumberFormat("es-CO", {
          style: "currency",
          currency: "COP",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(parseFloat(value)),
    },
    {
      title: "Precio (USD)",
      dataIndex: "precio_usd",
      key: "precio_usd",
      align: "right",
      render: (value) =>
        new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(parseFloat(value)),
    },
  ];

  const handleCancel = () => {
    Modal.confirm({
      title: "¿Está segur@ de que desea cerrar?",
      content:
        "Si cierra esta ventana, la información ingresada no se guardará.",
      okText: "Sí, cerrar",
      cancelText: "No",
      onOk: () => setIsModalOpen(false), // Cierra el modal
    });
  };

  // Función para cerrar el modal
  const handleCloseModalP = () => {
    setIsModalVisibleP(false);
  };

  return (
    <div className="container">
      <Header166 type="p" />
      <div className="d-flex justify-content-end align-items-center gap-3 mb-3">
        <div className="d-flex gap-3">
          <Input
            prefix={<SearchOutlined />}
            placeholder="Buscar por nombre o código"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            allowClear
          />
          <Select
            placeholder="Filtrar por categoría"
            style={{ width: 200 }}
            value={categoryFilter}
            onChange={(value) => setCategoryFilter(value)}
            allowClear
          >
            {categoriasUnicas.map((cat) => (
              <Select.Option key={cat} value={cat}>
                {cat}
              </Select.Option>
            ))}
          </Select>
        </div>
        <Button
          type="primary"
          onClick={() => {
            setIsEditing(false);
            setCurrentProducto(null);
            form.resetFields();
            setIsModalOpen(true);
          }}
        >
          + Nuevo producto
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={filteredProductos}
        rowKey="oid"
        loading={loading}
        bordered
        scroll={{ y: 500, x: "max-content" }}
        pagination={{
          current: currentPage, // página actual
          pageSize: pageSize, // filas por página
          total: filteredProductos.length, // total de registros
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} de ${total} registros`,
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          },
        }}
      />

      <Modal
        title={isEditing ? "Editar Producto" : "Nuevo Producto"}
        open={isModalOpen}
        onCancel={handleCancel}
        width="80%"
        style={{ top: 20 }}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="oid_categoria"
                label="Categoría"
                rules={[
                  { required: true, message: "La categoría es obligatoria." },
                ]}
              >
                <Select placeholder="Selecciona una categoría">
                  {categorias.map((cat) => (
                    <Select.Option key={cat.oid} value={cat.oid}>
                      {cat.nombreCategoria}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="oid_unidad_medida"
                label="Unidad de Medida"
                rules={[
                  {
                    required: true,
                    message: "La unidad de medida es obligatoria.",
                  },
                ]}
              >
                <Select placeholder="Selecciona unidad de medida">
                  {unidadesMedida.map((u) => (
                    <Select.Option key={u.oid} value={u.oid}>
                      {u.nombreUnidadMedida}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="nombre_producto"
            label="Nombre"
            rules={[{ required: true, message: "El nombre es obligatorio." }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="descripcion_producto"
            label="Descripción"
            rules={[
              { required: true, message: "La descripción es obligatoria." },
            ]}
          >
            <Input.TextArea
              rows={4}
              placeholder="Ingresa la descripción aquí"
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="codigo"
                label="Código"
                rules={[
                  { required: true, message: "El código es obligatorio." },
                  {
                    pattern: /^\S+$/,
                    message: "El código no puede contener espacios.",
                  },
                  {
                    // regla custom sin getFieldValue
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const currentOid = isEditing ? currentProducto.oid : null;
                      const exists = productos.some(
                        (p) => p.codigo === value && p.oid !== currentOid
                      );
                      return exists
                        ? Promise.reject(
                            new Error("Ese código ya existe en otro producto.")
                          )
                        : Promise.resolve();
                    },
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="precio_unitarioPesos"
                label="Precio Unitario (COP)"
                rules={[
                  {
                    required: true,
                    message: "El precio unitario es obligatorio.",
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  formatter={(v) =>
                    `$ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(v) => v.replace(/\$\s?|(,*)/g, "")}
                  min={1}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="precio_usd"
                label="Precio (USD)"
                rules={[
                  {
                    required: true,
                    message: "El precio en USD es obligatorio.",
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  formatter={(v) => v}
                  parser={(v) => v}
                  min={0}
                  step={0.01}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="clasificacion"
                label="Clasificación"
                rules={[
                  {
                    required: true,
                    message: "La clasificación es obligatoria.",
                  },
                ]}
              >
                <Select placeholder="Selecciona clasificación">
                  {["Fungible", "No fungible"].map((c) => (
                    <Select.Option key={c} value={c}>
                      {c}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="oid_fin_tipoProducto"
                label="Tipo de Producto"
                rules={[
                  {
                    required: true,
                    message: "El tipo de producto es obligatorio.",
                  },
                ]}
              >
                <Select placeholder="Selecciona tipo de producto">
                  {tiposProducto.map((t) => (
                    <Select.Option key={t.oid} value={t.oid}>
                      {t.nombreTipoProducto}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="unidad_tecnica"
                label="Unidad Técnica"
                rules={[
                  {
                    required: true,
                    message: "La unidad técnica es obligatoria.",
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="simbolo_proyecto" label="Símbolo Proyecto">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="estado" label="Estado">
                <Select placeholder="Selecciona estado" allowClear>
                  <Select.Option value="">Activo</Select.Option>
                  <Select.Option value="I">Inactivo</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <div style={{ textAlign: "right" }}>
            <Button type="primary" htmlType="submit">
              {isEditing ? "Actualizar" : "Crear"}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Modal para mostrar detalles del producto */}
      <Modal
        title={
          <span style={{ fontWeight: "bold", fontSize: "18px" }}>
            🛒 Detalles del Producto
          </span>
        }
        open={isModalVisibleP}
        onCancel={handleCloseModalP}
        width="60%"
        footer={[
          <Button key="close" type="primary" onClick={handleCloseModalP}>
            Cerrar
          </Button>,
        ]}
      >
        {selectedProductDetails && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              fontSize: "16px",
            }}
          >
            {/* Producto */}
            <div>
              <Tag color="blue">Producto: </Tag>
              <span style={{ fontWeight: "bold" }}>
                {" "}
                {selectedProductDetails.nombre_producto}
              </span>
            </div>

            {/* Descripción */}
            <div>
              <Tag color="green">Descripción:</Tag>
              <span> {selectedProductDetails.descripcion_producto}</span>
            </div>

            {/* Precio COP */}
            <div>
              <Tag color="gold">Precio de referencia COP: </Tag>
              <span style={{ fontWeight: "bold", color: "#198754" }}>
                {new Intl.NumberFormat("es-CO", {
                  style: "currency",
                  currency: "COP",
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }).format(selectedProductDetails.precio_unitarioPesos)}
              </span>
            </div>

            {/* Unidad de medida */}
            <div>
              <Tag color="red">Unidad de medida: </Tag>
              <span> {selectedProductDetails.nombreUnidadMedida}</span>
            </div>

            {/* Tipo */}
            <div>
              <Tag color="cyan">Categoría:</Tag>
              <span> {selectedProductDetails.nombreCategoria}</span>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ProductosSostenibilidad;
